import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';

import useMemberInfo, { IFragment } from '@hooks/useMemberInfo';
import { useMemberValidation } from '@hooks/useMemberValidation';

import { keysToCamel } from '@utils/butter';

import {
	IAuthenticatedMemberVisitDuration,
	IAuthenticatedMemberWithUnscheduledAppointments,
} from '@folx-types/member';
import { IUpcomingAppointments } from '@folx-types/scheduledAppointments';

import {
	unscheduledAppointments as UNSCHEDULED_APPOINTMENTS,
	unscheduledAppointmentsName,
} from '@graphql/fragments/unscheduledAppointments';
import {
	visitDuration as VISIT_DURATION,
	visitDurationName,
} from '@graphql/fragments/visitDuration';
import UPCOMING_APPOINTMENTS_QUERY from '@graphql/queries/UpcomingAppointments.query.gql';

import Appointments, { IAppointmentsData } from './Appointments.component';

interface IAppointmentsPageProps {
	path?: string;
}

const AppointmentsPage: FC<IAppointmentsPageProps> = (props) => {
	const pageData = useStaticQuery<Record<string, string>>(
		graphql`
			{
				butterPage(slug: { eq: "mssp-appointments" }) {
					title
					upcoming_heading
					no_upcoming_message
					no_upcoming_fineprint
					no_upcoming_fineprint_cta
					no_initial_error
					book_cta
					past_heading
				}
			}
		`
	);

	const visitDurationFragment: IFragment = {
		name: visitDurationName,
		node: VISIT_DURATION,
	};
	const unscheduledAppointmentsFragment: IFragment = {
		name: unscheduledAppointmentsName,
		node: UNSCHEDULED_APPOINTMENTS,
	};
	const { loading: memberLoading, memberInfo } = useMemberInfo<
		IAuthenticatedMemberWithUnscheduledAppointments & IAuthenticatedMemberVisitDuration
	>([unscheduledAppointmentsFragment, visitDurationFragment]);

	const unscheduledAppointments = memberInfo?.unscheduledAppointments;

	const serviceTypeDurationMap: Record<string, string> = memberInfo
		? {
				GAHT_T: memberInfo?.HRT_T,
				GAHT_E: memberInfo?.HRT_E,
				PRIMARY_CARE: memberInfo?.VC,
				MH_THERAPY: memberInfo?.MHT,
				MH_MEDICATION: memberInfo?.MHM,
		  }
		: {};

	const {
		loading: loadingUpcomingAppointments,
		error: errorUpcomingAppointments,
		data: upcomingAppointmentsData,
	} = useQuery<IUpcomingAppointments>(UPCOMING_APPOINTMENTS_QUERY, {
		fetchPolicy: 'network-only',
	});

	const { loaded: memberLoaded, memberMetadata } = useMemberValidation();

	const hasMembership = !memberMetadata?.membership?.isMembershipInactive;

	const pageContent: IAppointmentsData = keysToCamel(pageData) as IAppointmentsData;

	return (
		<Appointments
			upcomingAppointmentsData={upcomingAppointmentsData}
			loading={loadingUpcomingAppointments || memberLoading || !memberLoaded}
			hasMembership={hasMembership}
			pageContent={pageContent}
			unscheduledAppointments={unscheduledAppointments}
			serviceTypeDurationMap={serviceTypeDurationMap}
			errors={{
				noInitalAppointmentError: props?.location?.state?.noInitalAppointmentError,
				serviceType: props?.location?.state?.serviceType,
			}}
		/>
	);
};

export default AppointmentsPage;
